import React, { useState, useRef, useEffect } from 'react';
import { Layer, Stage, Text, Group, Line, Rect, Image as KonvaImage, Circle, Arrow, Shape } from 'react-konva';
import trainrailImageSrc from '../../../images/train-rail.png';
import tireImageSrc from '../../../images/pneumatico.png';
import texture2Image from '../../../images/stone-ballast-rail.png';
import texture3Image from '../../../images/subbase.png';
import texture4Image from '../../../images/subgrade.png';

// Interfacce per le Props
export interface ILayerData {
    name: string;
    thickness: number;
    color: string;
}

export interface IGeogridInfo {
    positionFromBottomOfBase: number; // Dal fondo del Base Course
    label?: string;
    color?: string;
}

/**
 * Props for Railway TireAndLayers component - compatible with Results.tsx
 */
interface Props {
    surfaceLayer?: ILayerData;
    baseCourse: ILayerData;
    subgrade: ILayerData; // Spessore = altezza visibile
    leftDimensionValue?: number;
    rightDimensionValue?: number;
    savingPercentage?: number;
    geogrid?: IGeogridInfo;
    unitLabel?: string;
    subbaseGeogrid?: IGeogridInfo;
    showLeftDimension?: boolean;
    showRightDimension?: boolean;
    showSavingBox?: boolean;
    showGeogrid?: boolean;
    showTire?: boolean;
    showLayers?: boolean;
    showGeotextile?: boolean;
    leftDimensionStrokeWidth?: number;
    horizontalSpacing?: number;
    verticalSpacing?: number;
    customStageHeight?: number;
    onStageHeightCalculated?: (height: number) => void;
    onSubgradeYCalculated?: (y: number) => void;
    alignFromBottom?: boolean;
    targetSubgradeY?: number;
    hideLayerTitles?: boolean;
    projectType?: 'container_yard' | 'parking_deck' | 'railways' | string;
    forcedStageWidth?: number;
    rectWidth?: number; // Larghezza dei rettangoli dei layer, opzionale per personalizzazione
    rectStartXOffset?: number;
    stageType?: 'geogrid' | 'geotextile' | 'unstabilized' | string;
    unstabilizedStageHeight?: number;
    // Railway-specific layer thickness props
    showLayerThickness?: boolean; // Show individual layer thickness values
    sleeperThickness?: number; // Railway sleeper thickness (from calculation data)
    ballastThickness?: number; // Railway ballast thickness (from calculation data)
    subballastThickness?: number; // Railway subballast thickness (from calculation data)
    setStabilisedBallastThickness?: number; // Railway set stabilised ballast thickness from form fields
}

// --- Componente ---
const TireAndLayers: React.FC<Props> = ({
    surfaceLayer,
    baseCourse,
    subgrade,
    savingPercentage,
    geogrid,
    unitLabel = 'm',
    showSavingBox = false,
    showGeogrid = false,
    showTire = true,
    showLayers = true,
    customStageHeight,
    onStageHeightCalculated,
    onSubgradeYCalculated,
    alignFromBottom = false,
    targetSubgradeY,
    hideLayerTitles = false,
    projectType = 'railways',
    forcedStageWidth,
    rectWidth = 320, // Aumenta larghezza rettangoli livelli
    // Railway-specific layer thickness props
    showLayerThickness = true, // Default to true for railways
    sleeperThickness,
    ballastThickness, // Display value (converted for Imperial)
    subballastThickness,
    setStabilisedBallastThickness,
}) => {
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [baseCourseTexture, setBaseCourseTexture] = useState<HTMLImageElement | null>(null);
    const [subgradeTexture, setSubgradeTexture] = useState<HTMLImageElement | null>(null);
    const [surfaceTexture, setSurfaceTexture] = useState<HTMLImageElement | null>(null);

    const stageRef = useRef<any>(null);

    // Image constants for railways
    const TRAIN_IMAGE_HEIGHT = 200 * 0.8;
    const TRAIN_IMAGE_WIDTH = 110 * 0.8;
    const TIRE_IMAGE_HEIGHT = 150 * 0.8;
    const TIRE_IMAGE_WIDTH = 68 * 0.8;

    // Determine which image to use based on project type
    const isParkingDeck = projectType === 'parking_deck';
    const isRailways = projectType === 'railways';
    const imageSrcToLoad = isParkingDeck ? tireImageSrc : isRailways ? trainrailImageSrc : tireImageSrc;
    const currentImageHeight = isParkingDeck ? TIRE_IMAGE_HEIGHT : isRailways ? TRAIN_IMAGE_HEIGHT : TIRE_IMAGE_HEIGHT;
    const currentImageWidth = isParkingDeck ? TIRE_IMAGE_WIDTH : isRailways ? TRAIN_IMAGE_WIDTH : TIRE_IMAGE_WIDTH;

    // Layout constants
    const IMAGE_TOP_PADDING = 10;
    const TITLE_AREA_WIDTH = 65;
    const LEFT_QUOTE_DIM_APRX = 65; // dimensione approssimativa per lo spazio delle quote

    // Calculate layer heights and positions
    const surfaceThickness = surfaceLayer?.thickness || 0;
    const baseCourseThickness = baseCourse.thickness;
    const subgradeThickness = subgrade.thickness;

    // For railways, use a scale factor to make layers visible
    const LAYER_SCALE_FACTOR = isRailways ? 110 : 1; // Aumenta altezza livelli
    const scaledSurfaceThickness = surfaceThickness * LAYER_SCALE_FACTOR;
    const scaledBaseCourseThickness = baseCourseThickness * LAYER_SCALE_FACTOR;
    const scaledSubgradeThickness = subgradeThickness * LAYER_SCALE_FACTOR;

    const totalLayerHeight = scaledSurfaceThickness + scaledBaseCourseThickness + scaledSubgradeThickness;
    const imageHeight = showTire ? currentImageHeight : 0;

    // Calculate stage dimensions
    const stageHeight = customStageHeight || IMAGE_TOP_PADDING + imageHeight + totalLayerHeight + 20;
    // const stageWidth = forcedStageWidth || (STAGE_PADDING_LEFT + TITLE_AREA_WIDTH + rectWidth + STAGE_PADDING_RIGHT);
    const stageWidth = forcedStageWidth || TITLE_AREA_WIDTH + rectWidth + LEFT_QUOTE_DIM_APRX;

    // Calculate Y positions
    let imageY: number = IMAGE_TOP_PADDING;
    let layersStartY: number, surfaceY: number, baseCourseY: number, subgradeY: number;

    if (alignFromBottom && targetSubgradeY !== undefined) {
        // Align from bottom - position subgrade at target Y and work upwards
        subgradeY = targetSubgradeY;
        baseCourseY = subgradeY - scaledBaseCourseThickness;
        surfaceY = baseCourseY - scaledSurfaceThickness;
        layersStartY = surfaceY;
        // Position image to rest on the surface layer (ballast)
        imageY = surfaceY - currentImageHeight;
        // console.log('[TireAndLayers] Entrata nel ramo alignFromBottom: surfaceY =', surfaceY, 'currentimageH = ', currentImageHeight);
    } else {
        // Default positioning - image at top, layers below
        layersStartY = imageY + imageHeight;
        surfaceY = layersStartY;
        baseCourseY = surfaceY + scaledSurfaceThickness;
        subgradeY = baseCourseY + scaledBaseCourseThickness;
    }

    // Calculate X positions
    // const rectStartX = STAGE_PADDING_LEFT + TITLE_AREA_WIDTH + rectStartXOffset;
    const rectStartX = TITLE_AREA_WIDTH + 10;
    const rectEndX = rectStartX + rectWidth;

    // For railways, center the train image on the ballast trapezoid top surface
    let imageX: number;
    if (isRailways && showLayers) {
        // Calculate ballast trapezoid top width for train centering
        // The trapezoid has a vertical right edge and sloped left edge
        const ballastHeight = scaledSurfaceThickness;
        const subballastHeight = scaledBaseCourseThickness;
        const totalTrapezoidHeight = ballastHeight + subballastHeight;
        const slopeRatio = 0.84; // 40 degrees angle
        const maxSlopeOffset = totalTrapezoidHeight * slopeRatio;
        // Ballast top surface: from (rectStartX + maxSlopeOffset) to (rectStartX + rectWidth)
        const ballastTopOffset = maxSlopeOffset;
        const ballastTopWidth = rectWidth - ballastTopOffset;
        const ballastStartX = rectStartX + ballastTopOffset;
        imageX = ballastStartX + ballastTopWidth / 2 - currentImageWidth / 2;
    } else {
        // Default centering for other project types
        imageX = rectStartX + rectWidth / 2 - currentImageWidth / 2;
    }

    // Debug logging for railways (can be removed in production)
    if (isRailways && showLayers) {
    }

    // Load images on component mount
    useEffect(() => {
        // Load main image (train or tire)
        const img = new window.Image();
        img.src = imageSrcToLoad;
        img.onload = () => setImage(img);
        img.onerror = () => {};

        // Load surface texture (se surface layer esiste, usa texture2Image)
        if (surfaceLayer) {
            const surfTex = new window.Image();
            surfTex.src = texture2Image;
            surfTex.onload = () => setSurfaceTexture(surfTex);
            surfTex.onerror = () => {};
        }

        // Load base course texture
        const baseTex = new window.Image();
        baseTex.src = texture3Image;
        baseTex.onload = () => setBaseCourseTexture(baseTex);
        baseTex.onerror = () => {};

        // Load subgrade texture
        const subTex = new window.Image();
        subTex.src = texture4Image;
        subTex.onload = () => setSubgradeTexture(subTex);
        subTex.onerror = () => {};
    }, [imageSrcToLoad, surfaceLayer]);

    // Notify parent of calculated heights
    useEffect(() => {
        if (onStageHeightCalculated) {
            onStageHeightCalculated(stageHeight);
        }
        if (onSubgradeYCalculated) {
            onSubgradeYCalculated(subgradeY);
        }
    }, [stageHeight, subgradeY, onStageHeightCalculated, onSubgradeYCalculated]);

    // Ref e stato per larghezza/altezza testo geogrid
    const geogridTextRef = useRef<any>(null);
    const [geogridTextWidth, setGeogridTextWidth] = useState(0);
    const [geogridTextHeight, setGeogridTextHeight] = useState(0);

    useEffect(() => {
        if (geogridTextRef.current) {
            setGeogridTextWidth(geogridTextRef.current.getTextWidth?.() || 0);
            setGeogridTextHeight(geogridTextRef.current.getTextHeight?.() || 0);
        }
    }, [geogrid?.label]);

    // Render saving percentage box
    const renderSavingBox = () => {
        if (!showSavingBox || typeof savingPercentage !== 'number') {
            return null;
        }

        // Stile e logica identici a paved
        const savingBoxWidth = 160;
        const savingBoxHeight = 70;
        const savingBoxX = rectStartX + rectWidth / 2 + TRAIN_IMAGE_WIDTH / 2 + 10; // Spostato di 40px a sinistra
        const savingBoxY = imageY;
        const borderColor = '#004652';
        const textColor = '#004652';

        return (
            <Group x={savingBoxX} y={savingBoxY} width={savingBoxWidth}>
                <Rect width={savingBoxWidth} height={savingBoxHeight} fill="white" stroke={borderColor} strokeWidth={3} cornerRadius={4} />
                <Text text={'Thickness Saving'} x={0} y={12} width={savingBoxWidth} align="center" fontSize={15} fill={textColor} fontStyle="italic" />
                <Text
                    text={`-${savingPercentage.toFixed(0)}%`}
                    width={savingBoxWidth}
                    height={savingBoxHeight}
                    align="center"
                    y={-8}
                    verticalAlign="bottom"
                    fontSize={30}
                    fill={textColor}
                    fontStyle="bold"
                />
            </Group>
        );
    };

    const geogridLabelPadding = 20;
    /////// posizine del label geogrid:
    const X_geogridLabelGroup = rectStartX + rectWidth / 2 - (geogridTextWidth + geogridLabelPadding) / 2;
    const Y_geogridLabelGroup = -(geogridTextHeight + geogridLabelPadding / 2);

    //const Y_geogridLabelGroup = - 9;

    //const Y_geogridLabelGroup = geogridTextHeight + (geogridLabelPadding / 2);
    //////// custom riga con pallini
    const nPoints = 6;
    const cerchioRaggio = 6;
    const circleSpacing = (rectEndX - (rectStartX + 0)) / (nPoints - 1);
    const circleXs = Array.from({ length: nPoints }, (_, i) => rectStartX - 6 + i * circleSpacing);

    // Render individual layer thickness dimensions
    const renderLayerThicknessDimensions = () => {
        if (!showLayerThickness || !isRailways) return null;

        const layerDimensions = [];
        const layerLineOffset = 15; // Distance from layer edge to dimension line

        // Surface layer (ballast for railways) thickness dimension
        if (surfaceLayer && scaledSurfaceThickness > 0) {
            // For railways: use ballastThickness (converted display value), fallback to surfaceLayer.thickness
            const actualThickness = ballastThickness || sleeperThickness || surfaceLayer.thickness;
            const layerCenterY = surfaceY + scaledSurfaceThickness / 2;
            const lineX = rectStartX + rectWidth + layerLineOffset;

            layerDimensions.push(
                <Group key="surface-dimension">
                    {/* Horizontal dimension line */}
                    <Line points={[rectStartX + rectWidth, surfaceY, lineX + 10, surfaceY]} stroke="#9e480e" strokeWidth={1} dash={[3, 2]} />
                    {/* <Line
                        points={[rectStartX + rectWidth, surfaceY + scaledSurfaceThickness, lineX + 15, surfaceY + scaledSurfaceThickness]}
                        stroke="#9e480e"
                        strokeWidth={2}
                        dash={[3, 2]}
                    /> */}
                    {/* Vertical dimension line */}
                    <Line points={[lineX, surfaceY, lineX, surfaceY + scaledSurfaceThickness]} stroke="#9e480e" strokeWidth={2} />
                    <Arrow
                        points={[lineX, surfaceY, lineX, surfaceY + scaledSurfaceThickness]}
                        stroke="#9e480e"
                        fill="#9e480e"
                        strokeWidth={1}
                        pointerAtBeginning={true}
                        pointerAtEnd={true}
                        pointerLength={3}
                        pointerWidth={5}
                    />
                    {/* Thickness text */}
                    <Text text={`${actualThickness.toFixed(2)} ${unitLabel}`} x={lineX + 15} y={layerCenterY - 6} fontSize={13} fill="black" fontStyle="bold" align="left" />
                </Group>
            );
        }

        // Base course (subballast for railways) thickness dimension
        if (scaledBaseCourseThickness > 0) {
            const actualThickness = subballastThickness || baseCourse.thickness;
            const layerCenterY = baseCourseY + scaledBaseCourseThickness / 2;
            const lineX = rectStartX + rectWidth + layerLineOffset;

            layerDimensions.push(
                <Group key="subballast-dimension">
                    {/* Horizontal dimension lines */}
                    <Line points={[rectStartX + rectWidth, baseCourseY, lineX + 10, baseCourseY]} stroke="#9e480e" strokeWidth={1} dash={[3, 2]} />
                    <Line
                        points={[rectStartX + rectWidth, baseCourseY + scaledBaseCourseThickness, lineX + 10, baseCourseY + scaledBaseCourseThickness]}
                        stroke="#9e480e"
                        strokeWidth={1}
                        dash={[3, 2]}
                    />
                    {/* Vertical dimension line */}
                    <Line points={[lineX, baseCourseY, lineX, baseCourseY + scaledBaseCourseThickness]} stroke="#9e480e" strokeWidth={2} />
                    <Arrow
                        points={[lineX, baseCourseY, lineX, baseCourseY + scaledBaseCourseThickness]}
                        stroke="#9e480e"
                        fill="#9e480e"
                        strokeWidth={1}
                        pointerAtBeginning={true}
                        pointerAtEnd={true}
                        pointerLength={10}
                        pointerWidth={5}
                    />
                    {/* Thickness text */}
                    <Text text={`${actualThickness.toFixed(2)} ${unitLabel}`} x={lineX + 15} y={layerCenterY - 6} fontSize={13} fill="black" fontStyle="bold" align="left" />
                </Group>
            );
        }

        // No dimension for subgrade - it's a fixed reference layer

        return layerDimensions;
    };

    // Render geogrid line with circles (like Container Yard)
    const renderGeogrid = () => {
        if (!showGeogrid || !geogrid) return null;

        const geogridColor = geogrid.color || '#24244f';

        // Calcolo Y delle basi layer
        const ballastBaseY = surfaceY + scaledSurfaceThickness; // base del Ballast
        const subballastBaseY = baseCourseY + scaledBaseCourseThickness; // base del Subballast

        // Calcolo X della base del Ballast (trapezio)
        const ballastHeight = scaledSurfaceThickness;
        const subballastHeight = scaledBaseCourseThickness;
        const totalTrapezoidHeight = ballastHeight + subballastHeight;
        const slopeRatio = 0.84;
        const maxSlopeOffset = totalTrapezoidHeight * slopeRatio;
        const bottomOffset = maxSlopeOffset - ballastHeight * slopeRatio;
        const ballastBaseStartX = rectStartX + bottomOffset;
        const ballastBaseEndX = rectEndX;

        // Se c'è subballast, disegna due griglie
        const hasSubballast = (setStabilisedBallastThickness || 0) > 0 && (subballastThickness || 0) > 0;

        // Geogrid superiore: solo all'interno del Ballast
        const renderBallastGeogrid = (yPosition: number, keyPrefix: string) => (
            <Group key={keyPrefix} y={yPosition}>
                <Line points={[ballastBaseStartX, 0, ballastBaseEndX, 0]} stroke={geogridColor} strokeWidth={3} shadowColor="#FFFFFF" opacity={0.8} lineCap="round" />
                {/* Cerchietti solo tra i limiti */}
                {circleXs
                    .filter(x => x + 6 >= ballastBaseStartX && x + 6 <= ballastBaseEndX)
                    .map((x, idx) => (
                        <Circle key={`${keyPrefix}-circle-${idx}`} x={x + 6} y={0} radius={cerchioRaggio} fill="white" stroke={geogridColor} strokeWidth={2} shadowColor="#FFFFFF" opacity={1} />
                    ))}
            </Group>
        );

        // Geogrid inferiore: da rectStartX a rectEndX
        const renderSubballastGeogrid = (yPosition: number, keyPrefix: string) => (
            <Group key={keyPrefix} y={yPosition}>
                <Line points={[rectStartX, 0, rectEndX, 0]} stroke={geogridColor} strokeWidth={3} shadowColor="#FFFFFF" opacity={0.8} lineCap="round" />
                {circleXs.map((x, idx) => (
                    <Circle key={`${keyPrefix}-circle-${idx}`} x={x + 6} y={0} radius={cerchioRaggio} fill="white" stroke={geogridColor} strokeWidth={2} shadowColor="#FFFFFF" opacity={1} />
                ))}
            </Group>
        );

        return (
            <Group>
                {/* Geogrid tra Ballast e Subballast, solo all'interno del Ballast */}
                {renderBallastGeogrid(ballastBaseY, 'geogrid-1')}

                {/* Geogrid tra Subballast e Subgrade (se presente) */}
                {hasSubballast && renderSubballastGeogrid(subballastBaseY, 'geogrid-2')}

                {/* Geogrid label - posizionato sulla prima griglia (ballast) */}
                {geogrid.label && (
                    <Group x={X_geogridLabelGroup} y={Y_geogridLabelGroup + ballastBaseY}>
                        <Rect
                            width={geogridTextWidth + geogridLabelPadding}
                            height={geogridTextHeight + geogridLabelPadding / 2}
                            fill="#FFFFFF"
                            cornerRadius={6}
                            x={0}
                            y={-10}
                            stroke="#828282"
                            strokeWidth={2}
                        />
                        <Text ref={geogridTextRef} text={geogrid.label} fontSize={12} fontStyle="bold" fill="#000000" x={geogridLabelPadding / 2} y={-4} />
                    </Group>
                )}

                {/* Geogrid label - posizionato sulla seconda griglia (subballast) se presente */}
                {hasSubballast && geogrid.label && (
                    <Group x={X_geogridLabelGroup} y={Y_geogridLabelGroup + subballastBaseY}>
                        <Rect
                            width={geogridTextWidth + geogridLabelPadding}
                            height={geogridTextHeight + geogridLabelPadding / 2}
                            fill="#FFFFFF"
                            cornerRadius={6}
                            x={0}
                            y={-10}
                            stroke="#828282"
                            strokeWidth={2}
                        />
                        <Text text={geogrid.label} fontSize={12} fontStyle="bold" fill="#000000" x={geogridLabelPadding / 2} y={-4} />
                    </Group>
                )}
            </Group>
        );
    };

    return (
        <Stage width={stageWidth} height={stageHeight} ref={stageRef}>
            <Layer>
                {/* Background giallo pallido per visualizzare l'area occupata dallo stage */}
                {/* <Rect x={0} y={0} width={stageWidth} height={stageHeight} fill='#FFF9C4' /> */}
                {/* Render layers if enabled */}
                {showLayers && (
                    <Group>
                        {/* Surface Layer - now shows ballast for railways */}
                        {surfaceLayer && scaledSurfaceThickness > 0 && (
                            <Group>
                                {!hideLayerTitles && (
                                    <Text text={surfaceLayer.name} x={0} y={surfaceY + scaledSurfaceThickness / 2 - 6} width={TITLE_AREA_WIDTH} align="right" fontSize={13} fill="black" />
                                )}
                                <Shape
                                    sceneFunc={(context, shape) => {
                                        // Trapezoid for ballast layer
                                        const ballastHeight = scaledSurfaceThickness;
                                        const subballastHeight = scaledBaseCourseThickness;
                                        const totalTrapezoidHeight = ballastHeight + subballastHeight;
                                        const slopeRatio = 0.84; // 40 degrees angle (tan(40°) ≈ 0.84)
                                        const maxSlopeOffset = totalTrapezoidHeight * slopeRatio;

                                        // Ballast trapezoid coordinates
                                        const topOffset = maxSlopeOffset;
                                        const bottomOffset = maxSlopeOffset - ballastHeight * slopeRatio;

                                        context.beginPath();
                                        context.moveTo(rectStartX + topOffset, surfaceY);
                                        context.lineTo(rectStartX + rectWidth, surfaceY);
                                        context.lineTo(rectStartX + rectWidth, surfaceY + ballastHeight);
                                        context.lineTo(rectStartX + bottomOffset, surfaceY + ballastHeight);
                                        context.closePath();

                                        // Apply fill color and texture
                                        if (surfaceTexture) {
                                            const pattern = context.createPattern(surfaceTexture, 'repeat');
                                            if (pattern) {
                                                context.fillStyle = pattern;
                                            }
                                        } else {
                                            context.fillStyle = surfaceLayer.color;
                                        }
                                        context.fill();

                                        // Apply stroke
                                        context.strokeStyle = 'grey';
                                        context.lineWidth = 0.5;
                                        context.stroke();

                                        context.fillStrokeShape(shape);
                                    }}
                                />
                            </Group>
                        )}

                        {/* Base Course Layer */}
                        {scaledBaseCourseThickness > 0 && (
                            <Group>
                                {!hideLayerTitles && (
                                    <Text text={baseCourse.name} x={0} y={baseCourseY + scaledBaseCourseThickness / 2 - 6} width={TITLE_AREA_WIDTH} align="right" fontSize={13} fill="black" />
                                )}
                                <Shape
                                    sceneFunc={(context, shape) => {
                                        // Trapezoid for subballast layer
                                        const ballastHeight = scaledSurfaceThickness;
                                        const subballastHeight = scaledBaseCourseThickness;
                                        const totalTrapezoidHeight = ballastHeight + subballastHeight;
                                        const slopeRatio = 0.84; // 40 degrees angle (tan(40°) ≈ 0.84)
                                        const maxSlopeOffset = totalTrapezoidHeight * slopeRatio;

                                        // Subballast trapezoid coordinates
                                        const topOffset = maxSlopeOffset - ballastHeight * slopeRatio;
                                        const bottomOffset = 0;

                                        context.beginPath();
                                        context.moveTo(rectStartX + topOffset, baseCourseY);
                                        context.lineTo(rectStartX + rectWidth, baseCourseY);
                                        context.lineTo(rectStartX + rectWidth, baseCourseY + subballastHeight);
                                        context.lineTo(rectStartX + bottomOffset, baseCourseY + subballastHeight);
                                        context.closePath();

                                        // Apply fill color and texture
                                        if (baseCourseTexture) {
                                            const pattern = context.createPattern(baseCourseTexture, 'repeat');
                                            if (pattern) {
                                                context.fillStyle = pattern;
                                                // Imposta la scala della texture
                                                context.setTransform(0.8, 0, 0, 0.8, 0, 0);
                                            }
                                        } else {
                                            context.fillStyle = baseCourse.color;
                                        }
                                        context.fill();

                                        // Apply stroke
                                        context.strokeStyle = 'grey';
                                        context.lineWidth = 0.5;
                                        context.stroke();

                                        context.fillStrokeShape(shape);
                                    }}
                                />
                            </Group>
                        )}

                        {/* Subgrade Layer */}
                        {scaledSubgradeThickness > 0 && (
                            <Group>
                                <Rect
                                // quanto occupa il testo titolo
                                // x={0}
                                // y={subgradeY + scaledSubgradeThickness / 2 - 6}
                                // width={TITLE_AREA_WIDTH}
                                // height={40}
                                // fill="red"
                                // stroke="grey"
                                // strokeWidth={0.5}
                                />
                                {!hideLayerTitles && (
                                    <Text text={subgrade.name} x={0} y={subgradeY + scaledSubgradeThickness / 2 - 6} width={TITLE_AREA_WIDTH} align="right" fontSize={13} fill="black" />
                                )}

                                <Rect
                                    x={rectStartX}
                                    y={subgradeY}
                                    width={rectWidth}
                                    height={scaledSubgradeThickness}
                                    // fill="red"
                                    fillPatternImage={subgradeTexture || undefined}
                                    fillPatternRepeat="repeat"
                                    fillPatternScale={{ x: 0.4, y: 0.4 }}
                                    stroke="grey"
                                    strokeWidth={0.5}
                                />
                            </Group>
                        )}
                    </Group>
                )}

                {/* Train/Tire Image */}
                {showTire && image && <KonvaImage image={image} x={imageX} y={imageY} width={currentImageWidth} height={currentImageHeight} />}

                {/* Geogrid */}
                {renderGeogrid()}

                {/* Layer thickness dimensions (for railways) */}
                {renderLayerThicknessDimensions()}

                {/* Saving box */}
                {renderSavingBox()}
            </Layer>
        </Stage>
    );
};

export default TireAndLayers;
